import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import {
  DailyNormsResponse,
  DimensionSetting,
  ExpertDailyNormsData,
  ExpertDailyNormsListItem,
  ExpertDailyNormsVersion,
  UpdateDailyNormsRequest,
  UpdateDimensionSettingPayload,
} from '../types/settings.types';

/**
 * Отримати налаштування одиниць виміру користувача для кожної вимірювальної величини
 */
export const getDimensionSettings = async (): Promise<DimensionSetting[]> => {
  const response = await api.get<ApiResponse<{ settings: DimensionSetting[] }>>('/settings/dimensions');
  const data = unwrapApiResponse<{ settings: DimensionSetting[] }>(response);

  if (data && Array.isArray(data.settings)) {
    return data.settings;
  }

  console.error("Неочікувана структура відповіді API для налаштувань вимірів:", data);
  return [];
};

/**
 * Оновити налаштування одиниць виміру користувача
 * @param settings - Масив налаштувань вимірів для оновлення
 */
export const updateDimensionSettings = async (settings: UpdateDimensionSettingPayload[]): Promise<void> => {
  await api.put('/settings/dimensions', { settings });
};

/**
 * Отримати щоденні цілі користувача
 */
export const getDailyNorms = async (): Promise<DailyNormsResponse> => {
  const response = await api.get<DailyNormsResponse>('/settings/daily-norms');
  return response.data;
};

/**
 * Оновити щоденні цілі користувача
 * @param goals - Щоденні цілі для оновлення
 */
export const updateDailyNorms = async (goals: UpdateDailyNormsRequest): Promise<void> => {
  await api.put('/settings/daily-norms', goals);
};

/**
 * Скинути щоденні цілі користувача до значень за замовчуванням
 */
export const resetDailyNorms = async (): Promise<void> => {
  await api.delete('/settings/daily-norms');
};

/**
 * Get daily goals for a specific client (for expert view)
 * @param clientId - The ID of the client
 */
export const getExpertClientDailyNorms = async (clientId: string): Promise<DailyNormsResponse> => {
    const response = await api.get<DailyNormsResponse>(`/client/${clientId}/settings/daily-norms`);
    return response.data;
};

export const getPmClientDailyNorms = async (clientId: number): Promise<DailyNormsResponse> => {
    const response = await api.get<DailyNormsResponse>(`/pm/clients/${clientId}/daily-norms`);
    return response.data;
};

/** Власні норми експерта для клієнта (лише свої). */
export const getExpertClientNorms = async (clientId: string | number): Promise<ExpertDailyNormsData> =>
  unwrapApiResponse<ExpertDailyNormsData>(await api.get(`/client/${clientId}/expert-norms`));

/** Версії власних норм, що діють у проміжку днів клієнта (yyyy-MM-dd). */
export const getExpertClientNormsHistory = async (
  clientId: string | number,
  from: string,
  to: string,
): Promise<ExpertDailyNormsVersion[]> =>
  unwrapApiResponse<ExpertDailyNormsVersion[]>(await api.get(`/client/${clientId}/expert-norms/history`, { params: { from, to } }));

/** Нова версія з сьогодні клієнта: лише передані поля; null - скинути до стандартної норми. */
export const updateExpertClientNorms = async (
  clientId: string | number,
  norms: UpdateDailyNormsRequest,
): Promise<ExpertDailyNormsData> =>
  unwrapApiResponse<ExpertDailyNormsData>(await api.put(`/client/${clientId}/expert-norms`, norms));

/** Власні норми всіх експертів клієнта - продакт. */
export const getPmClientExpertNorms = async (clientId: number): Promise<ExpertDailyNormsListItem[]> =>
  unwrapApiResponse<ExpertDailyNormsListItem[]>(await api.get(`/pm/clients/${clientId}/expert-norms`));

/** Власні норми всіх експертів клієнта - адмін. */
export const getAdminClientExpertNorms = async (clientId: number): Promise<ExpertDailyNormsListItem[]> =>
  unwrapApiResponse<ExpertDailyNormsListItem[]>(await api.get(`/admin/clients/${clientId}/expert-norms`));
